// // MODIFIED
import React, { useRef, useState } from "react";
import { Input, TextArea } from "../../../../common/Input";
import { Select } from "../../../../common/Select";
import { Button, GeneralMessage } from "../../../../common/APICommon";
import { LeaveBalanceSummary } from "./components/LeaveBalancesSummary";
import { useLeaveType } from "../../../hooks/useLeaveType";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { ConfirmationModal, ImageViewModal, PDFModal } from "../../../../common/Modal";

const LeaveRequestModule = ({
  user,
  userAccess,
  requests,
  formData,
  handleInputChange,
  handleFileChange,
  submitRequest,
  showMessage,
  message,
  isSuccess,
  loading,
  employeeLeaveCredits,
  fetchBalances
}) => {
  const currentEmployeePersonalInformationId = user?.employeePersonalInformationId;
  const fileInputRef = useRef(null);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { data: allLeaveTypes } = useLeaveType();

  const leaveTypeOptions = allLeaveTypes
    .filter(type => type.isActive)
    .map((type) => ({
      value: type.id,
      label: type.description,
    }));

  const selectedLeaveTypeName = leaveTypeOptions.find(opt => opt.value === formData.leaveTypeId)?.label || "Leave";

  const onFormSubmit = (e) => {
    e.preventDefault();
    const errors = {};

    if (!formData.leaveTypeId) errors.leaveTypeId = "Please select a leave type.";
    if (!formData.dateFrom) errors.dateFrom = "Start date is required.";
    if (!formData.dateTo) errors.dateTo = "End date is required.";

    if (formData.dateFrom && formData.dateTo) {
      if (new Date(formData.dateTo) < new Date(formData.dateFrom)) {
        errors.dateTo = "End date cannot be earlier than the start date.";
      }
    }

    const reasonTrimmed = formData.reason?.trim() || "";
    if (reasonTrimmed.length < 10) {
      errors.reason = "Reason must be at least 10 characters long.";
    } else if (reasonTrimmed.length > 500) {
      errors.reason = "Reason is too long (Max 500 characters).";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setErrorState(true);
      return;
    }

    setErrorState(false);
    setValidationErrors({});
    setIsConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    const payload = {
      ...formData,
      employeePersonalInformationId: currentEmployeePersonalInformationId,
      status: "Sent",
    };

    const success = await submitRequest(payload, currentEmployeePersonalInformationId, user.fullName);
    if (success) {
      if (fetchBalances) await fetchBalances();
      setIsConfirmOpen(false);
      setErrorState(false);
    }
  };

  const getFileUrl = () => {
    if (!formData.content) return "";
    return formData.fileType === "application/pdf"
      ? `data:application/pdf;base64,${formData.content}`
      : `data:image/jpeg;base64,${formData.content}`;
  };

  // // ADDED: Condition for disabling Half Day checkbox
  const isHalfDayDisabled = !formData.dateFrom || !formData.dateTo || formData.dateFrom !== formData.dateTo;

  return (
    <>
      <div className="space-y-6 p-4 relative">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          {employeeLeaveCredits.length > 0 && (
            <LeaveBalanceSummary employeeLeaveCredits={employeeLeaveCredits || []} />
          )}

          <form onSubmit={onFormSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Input label="Employee Name" type="text" value={user.employeePersonalInformationFullName} isDisabled={true} />

              <Select
                label="Leave Type"
                value={formData.leaveTypeId}
                onChange={(e) => handleInputChange('leaveTypeId', e.target.value)}
                options={leaveTypeOptions}
                isRequired
                placeholder="Choose a leave type"
                error={errorState && !!validationErrors.leaveTypeId}
                errorMessage={validationErrors.leaveTypeId}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Date From" type="date" value={formData.dateFrom}
                  onChange={(e) => handleInputChange('dateFrom', e.target.value)} isRequired
                  error={errorState && !!validationErrors.dateFrom}
                  errorMessage={validationErrors.dateFrom}
                />
                <Input
                  label="Date To" type="date" value={formData.dateTo}
                  onChange={(e) => handleInputChange('dateTo', e.target.value)} isRequired
                  error={errorState && !!validationErrors.dateTo}
                  errorMessage={validationErrors.dateTo}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input label="Days" type="number" placeholder="-" value={formData.days} readOnly />
                <div className="flex items-center mt-6">
                  {/* // MODIFIED: Checkbox disabled if multi-day selected */}
                  <input
                    type="checkbox"
                    checked={formData.isHalfDay}
                    onChange={(e) => handleInputChange('isHalfDay', e.target.checked)}
                    disabled={isHalfDayDisabled}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <label className={`ml-2 text-sm font-medium ${isHalfDayDisabled ? "text-gray-400" : "text-gray-600"}`}>
                    Half Day?
                  </label>
                </div>
              </div>
            </div>

            <div className="flex flex-col h-full justify-between">
              <label className="block text-sm font-medium text-gray-600 mb-1.5">File (Image or PDF):</label>
              <div
                className="flex-grow border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex flex-col items-center justify-center p-8 transition-colors hover:bg-gray-100 relative"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFileChange(e.dataTransfer.files[0]); }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleFileChange(file);
                  }}
                />

                <div className="flex flex-col items-center text-center">
                  <span className="text-gray-400 text-sm mb-2 font-medium">
                    {formData.fileName ? `Selected: ${formData.fileName}` : "No file selected"}
                  </span>

                  {formData.content && (
                    <button
                      type="button"
                      onClick={() => setIsPreviewOpen(true)}
                      className="flex items-center text-blue-500 hover:text-blue-700 text-xs font-bold mb-4 bg-transparent border-none cursor-pointer underline"
                    >
                      <RemoveRedEyeIcon fontSize="inherit" className="mr-1" />
                      PREVIEW FILE
                    </button>
                  )}
                </div>

                <button type="button" onClick={() => fileInputRef.current.click()} className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded shadow-sm border-none cursor-pointer transition-colors">
                  Upload File
                </button>
                <p className="text-[10px] text-gray-400 mt-4 uppercase font-bold tracking-widest">
                  Supported formats: JPG, PNG, PDF (Max 2MB)
                </p>
              </div>
            </div>

            <div className="md:col-span-2">
              <TextArea
                label="Reason"
                isRequired
                placeholder="Please explain the reason for your leave (Min 10 characters)..."
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                error={errorState && !!validationErrors.reason}
                errorMessage={validationErrors.reason}
                rows={4}
              />
            </div>

            <div className="md:col-span-2 flex justify-end">
              <Button name="Send Request" className="bg-blue-600 text-white hover:bg-blue-700 px-8 py-2.5 font-bold" type="submit" />
            </div>
          </form>
        </div>

        {formData.fileType === "application/pdf" ? (
          <PDFModal
            visible={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            fileUrl={getFileUrl()}
          />
        ) : (
          <ImageViewModal
            isOpen={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            fileData={formData.content}
            fileType={formData.fileType}
          />
        )}
      </div>

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />

      <ConfirmationModal
        visible={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        title="Confirm Leave Submission"
        message={`Are you sure you want to file ${formData.days} day(s) of ${selectedLeaveTypeName}?`}
        noteMessage="Your remaining balance will be deducted immediately upon submission."
        confirmText="Confirm & Submit"
        type="info"
        isLoading={loading}
      />
    </>
  );
};

export default LeaveRequestModule;